import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon,
  BoltIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ClockIcon,
  Cog6ToothIcon,
  CubeIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

const BICICLO_LOGO_SRC = '/branding/biciclo-logo.png?v=20260605-2';
const HERO_IMAGE_SRC = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=2200&q=85';

const CONTACT = {
  phoneDisplay: '07 68 76 27 80',
  phoneHref: 'tel:+33768762780',
  smsHref: 'sms:+33768762780',
};

const heroStats = [
  { value: '100+', label: 'ateliers équipés' },
  { value: '54 EUR', label: 'offre de départ' },
  { value: '6j/7', label: 'support possible' },
];

const experiences = [
  {
    id: 'atelier',
    title: 'Atelier',
    subtitle: 'Réparer sans perdre le fil',
    icon: WrenchScrewdriverIcon,
    accentClass: 'bg-[#34c759]',
    softClass: 'bg-[#effaf2]',
    borderClass: 'border-[#34c759]',
    textClass: 'text-[#147a35]',
    headline: 'Chaque réparation avance avec la bonne information.',
    metric: '18',
    metricUnit: 'ordres prêts',
    chips: ['Diagnostic', 'Pièces', 'SMS client'],
    rows: [
      { label: 'Réception', value: '9 vélos', width: '76%' },
      { label: 'Diagnostic', value: '6 devis', width: '58%' },
      { label: 'Livraison', value: '18 prêts', width: '88%' },
    ],
    timeline: [
      'Rendez-vous synchronisé',
      'Pièces associées au ticket',
      'Client notifié automatiquement',
    ],
  },
  {
    id: 'vente',
    title: 'Vente',
    subtitle: 'Encaisser, réserver, convertir',
    icon: ShoppingBagIcon,
    accentClass: 'bg-[#0071e3]',
    softClass: 'bg-[#eef6ff]',
    borderClass: 'border-[#0071e3]',
    textClass: 'text-[#005bb5]',
    headline: 'Le comptoir devient rapide, clair et rassurant.',
    metric: '2.4k',
    metricUnit: 'EUR en panier',
    chips: ['Caisse', 'TPE', 'Réservations'],
    rows: [
      { label: 'Paniers actifs', value: '7', width: '68%' },
      { label: 'Devis ouverts', value: '11', width: '82%' },
      { label: 'Retours suivis', value: '2', width: '34%' },
    ],
    timeline: [
      'Disponibilité produit visible',
      'Acompte relié au devis',
      'Encaissement connecté',
    ],
  },
  {
    id: 'stock',
    title: 'Stock',
    subtitle: 'Acheter mieux, servir plus vite',
    icon: CubeIcon,
    accentClass: 'bg-[#ff9f0a]',
    softClass: 'bg-[#fff6e6]',
    borderClass: 'border-[#ff9f0a]',
    textClass: 'text-[#9a5a00]',
    headline: 'Le stock central et les fournisseurs restent visibles.',
    metric: '250+',
    metricUnit: 'fournisseurs',
    chips: ['P2R', 'Transferts', 'Alertes'],
    rows: [
      { label: 'Stock central', value: '31 alertes', width: '46%' },
      { label: 'Transferts', value: '12 en cours', width: '62%' },
      { label: 'Commandes', value: '5 à valider', width: '78%' },
    ],
    timeline: [
      'Fournisseurs connectés',
      'Réassort conseillé',
      'Vision multi-magasins',
    ],
  },
  {
    id: 'pilotage',
    title: 'Pilotage',
    subtitle: 'Décider avec une vue nette',
    icon: ChartBarIcon,
    accentClass: 'bg-[#5856d6]',
    softClass: 'bg-[#f2f1ff]',
    borderClass: 'border-[#5856d6]',
    textClass: 'text-[#3634a3]',
    headline: 'Votre activité devient lisible en temps réel.',
    metric: '94%',
    metricUnit: 'flux suivis',
    chips: ['Marge', 'Compta', 'Sites'],
    rows: [
      { label: 'Marge', value: '+12%', width: '72%' },
      { label: 'Charge atelier', value: '87%', width: '87%' },
      { label: 'Objectif jour', value: 'en avance', width: '64%' },
    ],
    timeline: [
      'Vision consolidée',
      'Facturation électronique',
      'Suivi comptable',
    ],
  },
];

const offers = [
  {
    id: 'offer1',
    name: 'Offre 1',
    price: '54 EUR',
    line: 'Le socle complet pour lancer un ERP moderne en magasin.',
    badge: 'Point d’entrée',
    includes: [
      'Interface moderne et simple',
      'Gestion multi-magasins',
      'Stock central et transferts',
      'Caisse connectée TPE',
      'SMS + Email illimités',
      'P2R fournisseur connecté',
      'Reprise des données clients',
      'Facturation électronique disponible',
    ],
  },
  {
    id: 'offer2',
    name: 'Offre 2',
    price: '65 EUR',
    line: 'La configuration renforcée pour aller plus loin dans l’écosystème.',
    badge: 'Le plus complet',
    includes: [
      'Tout ce qui est inclus dans l’offre 1',
      'Nom de domaine custom',
      '+250 fournisseurs connectés',
      'SMS avec senderId custom',
      'Support informatique 6j/7',
      'Hotline dédiée',
      'Vision consolidée avancée',
      'Accompagnement renforcé',
    ],
  },
];

const comparisonRows = [
  { feature: 'Multi-magasins, stock central et transferts', offer1: true, offer2: true },
  { feature: 'Caisse connectée avec TPE SumUp, Qonto, etc.', offer1: true, offer2: true },
  { feature: 'Rendez-vous, atelier, devis et notifications client', offer1: true, offer2: true },
  { feature: 'Suivi de comptabilité et facturation électronique disponible', offer1: true, offer2: true },
  { feature: 'P2R fournisseur connecté', offer1: true, offer2: true },
  { feature: '+250 fournisseurs connectés pour les commandes', offer1: false, offer2: true },
  { feature: 'Nom de domaine custom', offer1: false, offer2: true },
  { feature: 'Support informatique 6j/7 + hotline dédiée', offer1: false, offer2: true },
];

const partners = [
  { name: 'Qonto', description: 'Services financiers professionnels', panelClass: 'bg-white text-[#1d1d1f] border border-[#d2d2d7]' },
  { name: 'SumUp', description: 'Terminaux et encaissement connecté', panelClass: 'bg-[#1d1d1f] text-white' },
  { name: 'P2R', description: 'Fournisseur connecté pour pièces et commandes', panelClass: 'bg-white text-[#d62828] border border-[#d2d2d7]' },
  { name: 'KTM', description: 'Marque reconnue de l’univers vélo', panelClass: 'bg-[#ff6b00] text-[#1d1d1f]' },
  { name: 'SHIMANO', description: 'Composants et équipements cycle', panelClass: 'bg-[#005baa] text-white' },
];

const gallery = [
  {
    src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80',
    alt: 'Boutique de vélos avec espace de vente',
    title: 'Une boutique qui vend mieux',
  },
  {
    src: 'https://images.unsplash.com/photo-1507035895480-2b3156c31fc8?auto=format&fit=crop&w=1200&q=80',
    alt: 'Vélo sportif en préparation',
    title: 'Un atelier qui avance plus vite',
  },
];

const trustItems = [
  { icon: ShieldCheckIcon, label: 'Accès sécurisé' },
  { icon: ClockIcon, label: 'Prise en main rapide' },
  { icon: SparklesIcon, label: 'Expertise métier offerte' },
];

function CheckMark({ active = true }) {
  return active ? (
    <CheckCircleIcon className="mx-auto h-6 w-6 text-[#34c759]" aria-hidden="true" />
  ) : (
    <span className="mx-auto block h-0.5 w-5 rounded-full bg-[#d2d2d7]" />
  );
}

function ProductPreview({ activeExperience }) {
  const ActiveIcon = activeExperience.icon;

  return (
    <div className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-[30px] border-[10px] border-[#1d1d1f] bg-[#f5f5f7] shadow-[0_40px_120px_rgba(0,0,0,0.32)]">
      <div className="flex items-center justify-between border-b border-[#d2d2d7] bg-white px-5 py-4">
        <div className="flex items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${activeExperience.accentClass} text-white`}>
            <ActiveIcon className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-[#1d1d1f]">{activeExperience.title}</p>
            <p className="text-xs text-[#6e6e73]">{activeExperience.subtitle}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-[#f5f5f7] px-3 py-1.5 text-xs font-semibold text-[#424245]">
          <span className={`h-2 w-2 rounded-full ${activeExperience.accentClass}`} />
          Live
        </span>
      </div>

      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1.08fr)_minmax(17rem,0.92fr)]">
        <section className="rounded-lg bg-white p-5 shadow-[0_12px_34px_rgba(29,29,31,0.08)]">
          <p className="text-sm font-medium text-[#6e6e73]">Vue du jour</p>
          <div className="mt-3 flex flex-wrap items-baseline gap-3">
            <p className="text-5xl font-semibold text-[#1d1d1f]">{activeExperience.metric}</p>
            <p className="text-base text-[#6e6e73]">{activeExperience.metricUnit}</p>
          </div>

          <div className="mt-7 space-y-5">
            {activeExperience.rows.map((row) => (
              <div key={row.label}>
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="font-medium text-[#424245]">{row.label}</span>
                  <span className="text-[#6e6e73]">{row.value}</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-[#e5e5ea]">
                  <div
                    className={`h-2 rounded-full ${activeExperience.accentClass} transition-all duration-500`}
                    style={{ width: row.width }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={`rounded-lg p-5 ${activeExperience.softClass}`}>
          <div className="flex items-center gap-2">
            <BoltIcon className="h-5 w-5 text-[#1d1d1f]" aria-hidden="true" />
            <p className="text-sm font-semibold text-[#1d1d1f]">Actions prêtes</p>
          </div>
          <div className="mt-5 space-y-3">
            {activeExperience.timeline.map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-lg bg-white px-3 py-3 text-sm font-medium text-[#424245] shadow-sm">
                <CheckCircleIcon className={`h-5 w-5 ${activeExperience.textClass}`} aria-hidden="true" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {activeExperience.chips.map((chip) => (
              <span key={chip} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${activeExperience.softClass} ${activeExperience.textClass}`}>
                {chip}
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [activeExperienceId, setActiveExperienceId] = useState(experiences[0].id);
  const [activeOfferId, setActiveOfferId] = useState(offers[1].id);
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    phone: '',
    message: '',
  });

  const activeExperience = experiences.find((experience) => experience.id === activeExperienceId) || experiences[0];
  const activeOffer = offers.find((offer) => offer.id === activeOfferId) || offers[1];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const lines = [
      `Nom : ${formData.fullName}`,
      `Société : ${formData.company || 'Non renseignée'}`,
      `Téléphone : ${formData.phone || 'Non renseigné'}`,
      '',
      'Message :',
      formData.message,
    ];

    const body = encodeURIComponent(lines.join('\n'));

    window.location.href = `${CONTACT.smsHref}?body=${body}`;
    toast.success('Votre demande est prête dans votre application de message.');

    setFormData({
      fullName: '',
      company: '',
      phone: '',
      message: '',
    });
  };

  return (
    <main className="min-h-screen overflow-hidden bg-[#f5f5f7] text-[#1d1d1f]">
      <section className="relative min-h-[88vh] overflow-hidden bg-[#1d1d1f] text-white">
        <img
          src={HERO_IMAGE_SRC}
          alt="Atelier vélo moderne avec vélos et établi"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.84)_0%,rgba(0,0,0,0.48)_46%,rgba(0,0,0,0.12)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(180deg,rgba(0,0,0,0),#f5f5f7)]" />

        <header className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-5 lg:px-8">
          <Link to="/" className="flex h-12 w-36 items-center justify-center overflow-hidden rounded-full border border-white/12 bg-[#1d1d1f] px-4 shadow-[0_16px_40px_rgba(0,0,0,0.28)]">
            <img
              src={BICICLO_LOGO_SRC}
              alt="Logo Biciclo ERP"
              className="h-full w-auto max-w-none object-contain"
            />
          </Link>

          <nav className="hidden items-center gap-7 text-sm font-medium text-white/78 lg:flex">
            <a href="#configuration" className="transition hover:text-white">Configurer</a>
            <a href="#offres" className="transition hover:text-white">Offres</a>
            <a href="#ecosysteme" className="transition hover:text-white">Écosystème</a>
            <a href="#contact" className="transition hover:text-white">Contact</a>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden rounded-full border border-white/22 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/18 sm:inline-flex"
            >
              Connexion ERP
            </Link>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#1d1d1f] shadow-[0_16px_40px_rgba(0,0,0,0.2)] transition hover:bg-[#f5f5f7]"
            >
              <PhoneIcon className="h-4 w-4" aria-hidden="true" />
              Appeler
            </a>
          </div>
        </header>

        <div className="relative z-10 mx-auto flex min-h-[calc(88vh-5.5rem)] w-full max-w-7xl flex-col justify-center px-5 pb-16 pt-12 lg:px-8">
          <p className="text-sm font-semibold uppercase text-white/78">ERP vélo, atelier et commerce</p>
          <h1 className="mt-5 max-w-4xl text-5xl font-semibold leading-none text-white sm:text-6xl lg:text-7xl">
            Biciclo ERP
          </h1>
          <p className="mt-6 max-w-2xl text-xl leading-8 text-white/82">
            Une expérience premium pour piloter la vente, l’atelier, le stock et les fournisseurs comme un seul produit.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href="#configuration"
              className="inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#1d1d1f] shadow-[0_20px_50px_rgba(0,0,0,0.22)] transition hover:bg-[#f5f5f7]"
            >
              Composer mon ERP
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-full border border-white/28 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/18"
            >
              Parler à un expert
            </a>
          </div>

          <div className="mt-12 grid max-w-2xl gap-3 sm:grid-cols-3">
            {heroStats.map((stat) => (
              <div key={stat.label} className="border-t border-white/28 pt-4">
                <p className="text-2xl font-semibold text-white">{stat.value}</p>
                <p className="mt-1 text-sm text-white/72">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="configuration" className="mx-auto -mt-10 w-full max-w-7xl px-5 pb-20 lg:px-8">
        <div className="relative z-20 rounded-lg border border-white bg-white p-4 shadow-[0_30px_90px_rgba(29,29,31,0.14)] lg:p-6">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.9fr)]">
            <ProductPreview activeExperience={activeExperience} />

            <div className="flex flex-col justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-[#0071e3]">Choisissez votre expérience</p>
                <h2 className="mt-2 text-3xl font-semibold leading-tight text-[#1d1d1f]">
                  Configurez l’ERP autour de votre priorité.
                </h2>
                <p className="mt-3 text-base leading-7 text-[#6e6e73]">
                  Comme un produit Apple, chaque choix montre immédiatement ce que votre équipe gagne au quotidien.
                </p>
              </div>

              <div className="grid gap-3">
                {experiences.map((experience) => {
                  const ExperienceIcon = experience.icon;
                  const isActive = activeExperienceId === experience.id;

                  return (
                    <button
                      key={experience.id}
                      type="button"
                      data-testid={`experience-${experience.id}`}
                      onClick={() => setActiveExperienceId(experience.id)}
                      aria-pressed={isActive}
                      className={`flex min-h-[5rem] items-center gap-3 rounded-lg border p-4 text-left transition ${
                        isActive
                          ? `${experience.borderClass} ${experience.softClass} shadow-[0_18px_44px_rgba(29,29,31,0.1)]`
                          : 'border-[#d2d2d7] bg-white hover:border-[#a1a1a6]'
                      }`}
                    >
                      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${isActive ? experience.accentClass : 'bg-[#f5f5f7]'} ${isActive ? 'text-white' : 'text-[#424245]'}`}>
                        <ExperienceIcon className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <span>
                        <span className="block text-base font-semibold text-[#1d1d1f]">{experience.title}</span>
                        <span className="mt-1 block text-sm text-[#6e6e73]">{experience.subtitle}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-[#d2d2d7] bg-white py-20">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:px-8">
          <div>
            <p className="text-sm font-semibold text-[#0071e3]">Pourquoi ça vend mieux</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#1d1d1f]">
              Le discours devient simple: tout est connecté.
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#6e6e73]">
              Biciclo ERP rassemble le comptoir, l’atelier, les fournisseurs, le stock, les rendez-vous et la facturation électronique dans une interface lisible.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {[
              { icon: BuildingStorefrontIcon, title: 'Magasin', text: 'Vente, devis, réservations et encaissement restent alignés.' },
              { icon: WrenchScrewdriverIcon, title: 'Atelier', text: 'Tickets, pièces, priorités et notifications client avancent ensemble.' },
              { icon: CircleStackIcon, title: 'Pilotage', text: 'Stock, comptabilité, multi-sites et fournisseurs deviennent visibles.' },
            ].map((item) => {
              const ItemIcon = item.icon;

              return (
                <article key={item.title} className="rounded-lg border border-[#d2d2d7] bg-[#fbfbfd] p-5">
                  <ItemIcon className="h-7 w-7 text-[#0071e3]" aria-hidden="true" />
                  <h3 className="mt-5 text-xl font-semibold text-[#1d1d1f]">{item.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#6e6e73]">{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="offres" className="mx-auto w-full max-w-7xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold text-[#0071e3]">Choisissez votre configuration</p>
          <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#1d1d1f]">
            Deux offres claires. Une décision rapide.
          </h2>
          <p className="mt-4 text-lg leading-8 text-[#6e6e73]">
            Sélectionnez une offre et voyez immédiatement ce qui est inclus. Le plus simple reste ensuite un appel pour valider la bonne configuration.
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-2">
          {offers.map((offer) => {
            const isActive = activeOfferId === offer.id;

            return (
              <button
                key={offer.id}
                type="button"
                data-testid={`offer-${offer.id}`}
                onClick={() => setActiveOfferId(offer.id)}
                aria-pressed={isActive}
                className={`rounded-lg border p-6 text-left transition ${
                  isActive
                    ? 'border-[#0071e3] bg-white shadow-[0_24px_70px_rgba(0,113,227,0.16)]'
                    : 'border-[#d2d2d7] bg-white/72 hover:border-[#a1a1a6]'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-[#0071e3]">{offer.badge}</p>
                    <h3 className="mt-2 text-3xl font-semibold text-[#1d1d1f]">{offer.name}</h3>
                  </div>
                  <p className="text-3xl font-semibold text-[#1d1d1f]">{offer.price}</p>
                </div>
                <p className="mt-4 text-base leading-7 text-[#6e6e73]">{offer.line}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid gap-6 rounded-lg border border-[#d2d2d7] bg-white p-5 shadow-[0_20px_60px_rgba(29,29,31,0.08)] lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
          <div className="rounded-lg bg-[#f5f5f7] p-5">
            <div className="flex items-center gap-3">
              <Cog6ToothIcon className="h-6 w-6 text-[#0071e3]" aria-hidden="true" />
              <p className="text-sm font-semibold text-[#0071e3]">Configuration sélectionnée</p>
            </div>
            <h3 className="mt-4 text-3xl font-semibold text-[#1d1d1f]">{activeOffer.name}</h3>
            <p className="mt-2 text-5xl font-semibold text-[#1d1d1f]">{activeOffer.price}</p>
            <p className="mt-4 text-base leading-7 text-[#6e6e73]">{activeOffer.line}</p>
            <a
              href={CONTACT.phoneHref}
              className="mt-6 inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-full bg-[#0071e3] px-6 py-3 text-sm font-semibold text-white shadow-[0_16px_40px_rgba(0,113,227,0.22)] transition hover:bg-[#005bb5]"
            >
              Valider par téléphone
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {activeOffer.includes.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-lg border border-[#e5e5ea] bg-white px-4 py-3">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#34c759]" aria-hidden="true" />
                <span className="text-sm font-medium leading-6 text-[#424245]">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 overflow-hidden rounded-lg border border-[#d2d2d7] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="bg-[#1d1d1f] text-white">
                  <th className="px-5 py-5 text-left text-sm font-semibold">Fonctionnalités</th>
                  <th className="border-l border-white/12 px-5 py-5 text-center text-sm font-semibold">Offre 1</th>
                  <th className="border-l border-white/12 px-5 py-5 text-center text-sm font-semibold">Offre 2</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.feature} className="border-t border-[#e5e5ea]">
                    <td className="px-5 py-4 text-sm leading-6 text-[#424245]">{row.feature}</td>
                    <td className="border-l border-[#e5e5ea] px-5 py-4 text-center">
                      <CheckMark active={row.offer1} />
                    </td>
                    <td className="border-l border-[#e5e5ea] px-5 py-4 text-center">
                      <CheckMark active={row.offer2} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section id="ecosysteme" className="bg-[#1d1d1f] py-20 text-white">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:px-8">
          <div>
            <p className="text-sm font-semibold text-white/68">Écosystème connecté</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight">
              Votre ERP parle le langage du vélo et du paiement.
            </h2>
            <p className="mt-5 text-lg leading-8 text-white/68">
              Qonto, SumUp, P2R, KTM et Shimano renforcent la crédibilité de l’offre et projettent le client dans son quotidien réel.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {trustItems.map((item) => {
                const TrustIcon = item.icon;

                return (
                  <div key={item.label} className="rounded-lg border border-white/12 bg-white/8 px-4 py-4">
                    <TrustIcon className="h-6 w-6 text-[#34c759]" aria-hidden="true" />
                    <p className="mt-3 text-sm font-semibold text-white">{item.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {partners.map((partner) => (
              <article key={partner.name} className="rounded-lg border border-white/12 bg-white/8 p-4">
                <div className={`flex h-20 items-center justify-center rounded-lg px-4 text-2xl font-black ${partner.panelClass}`}>
                  {partner.name}
                </div>
                <p className="mt-4 text-sm leading-6 text-white/68">{partner.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-2">
          {gallery.map((item) => (
            <article key={item.title} className="overflow-hidden rounded-lg border border-[#d2d2d7] bg-white shadow-[0_18px_55px_rgba(29,29,31,0.08)]">
              <img
                src={item.src}
                alt={item.alt}
                className="h-80 w-full object-cover"
                loading="lazy"
              />
              <div className="p-5">
                <p className="text-sm font-semibold text-[#0071e3]">Projection terrain</p>
                <h3 className="mt-2 text-2xl font-semibold text-[#1d1d1f]">{item.title}</h3>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="contact" className="border-t border-[#d2d2d7] bg-white py-20">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-5 lg:grid-cols-[minmax(0,0.85fr)_minmax(24rem,1.15fr)] lg:px-8">
          <div>
            <p className="text-sm font-semibold text-[#0071e3]">Contact</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#1d1d1f]">
              Une belle page doit surtout donner envie d’aller plus loin.
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#6e6e73]">
              Le plus rapide: un appel. En quelques minutes, vous pouvez valider l’offre, les modules prioritaires et le niveau d’accompagnement utile.
            </p>

            <div className="mt-8 grid gap-4">
              <a href={CONTACT.phoneHref} className="flex items-center justify-between gap-4 rounded-lg border border-[#d2d2d7] bg-[#f5f5f7] px-5 py-4 transition hover:bg-white">
                <span>
                  <span className="block text-sm font-semibold text-[#6e6e73]">Appel direct</span>
                  <span className="mt-1 block text-2xl font-semibold text-[#1d1d1f]">{CONTACT.phoneDisplay}</span>
                </span>
                <PhoneIcon className="h-6 w-6 text-[#0071e3]" aria-hidden="true" />
              </a>
              <div className="rounded-lg border border-[#d2d2d7] bg-white px-5 py-4">
                <div className="flex items-center gap-3">
                  <DevicePhoneMobileIcon className="h-6 w-6 text-[#0071e3]" aria-hidden="true" />
                  <p className="font-semibold text-[#1d1d1f]">SMS préparé automatiquement</p>
                </div>
                <p className="mt-2 text-sm leading-6 text-[#6e6e73]">
                  Le formulaire prépare votre demande dans l’application de message de votre appareil.
                </p>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-[#d2d2d7] bg-[#fbfbfd] p-5 shadow-[0_20px_60px_rgba(29,29,31,0.08)] sm:p-6"
          >
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-[#0071e3]" aria-hidden="true" />
                <p className="text-sm font-semibold text-[#0071e3]">Être recontacté</p>
              </div>
              <h3 className="mt-3 text-3xl font-semibold leading-tight text-[#1d1d1f]">
                Décrivez votre magasin.
              </h3>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="sm:col-span-1">
                <span className="mb-2 block text-sm font-semibold text-[#424245]">Nom complet</span>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="app-input"
                  placeholder="Votre nom"
                  autoComplete="name"
                />
              </label>

              <label className="sm:col-span-1">
                <span className="mb-2 block text-sm font-semibold text-[#424245]">Société</span>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="app-input"
                  placeholder="Nom de votre structure"
                  autoComplete="organization"
                />
              </label>

              <label className="sm:col-span-1">
                <span className="mb-2 block text-sm font-semibold text-[#424245]">Téléphone</span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="app-input"
                  placeholder="06 00 00 00 00"
                  autoComplete="tel"
                />
              </label>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-semibold text-[#424245]">Votre besoin</span>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="app-input resize-none"
                placeholder="Nombre de magasins, priorité atelier ou vente, stock, fournisseurs, reprise de données..."
              />
            </label>

            <button
              type="submit"
              className="mt-6 inline-flex min-h-[3rem] w-full items-center justify-center gap-2 rounded-full bg-[#0071e3] px-6 py-3 text-sm font-semibold text-white shadow-[0_16px_40px_rgba(0,113,227,0.22)] transition hover:bg-[#005bb5]"
            >
              Préparer la demande
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </div>
      </section>

      <footer className="bg-[#1d1d1f] px-5 py-8 text-white lg:px-8">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 text-sm text-white/64 sm:flex-row sm:items-center sm:justify-between">
          <span>Biciclo ERP</span>
          <div className="flex flex-wrap gap-4">
            <a href={CONTACT.phoneHref} className="transition hover:text-white">{CONTACT.phoneDisplay}</a>
            <Link to="/login" className="transition hover:text-white">Connexion ERP</Link>
            <a href="#configuration" className="transition hover:text-white">Configurer</a>
          </div>
        </div>
      </footer>
    </main>
  );
}
